import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PurchaseRequest, Ticket } from '../types'
import { useAuth } from '../hooks/useAuth'
import { 
  loadPurchaseRequests, 
  approvePurchaseRequestByDirector,
  rejectPurchaseRequestByDirector,
  loadTickets,
  accelerateTicket
} from '../lib/api'
import PurchaseRequestsTable from '../components/PurchaseRequestsTable'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import TicketList from '../components/TicketList'
import TicketsTable from '../components/TicketsTable'
import Pagination from '../components/Pagination'
import ExecutorStatistics from '../components/ExecutorStatistics'
import { useIsMobile } from '../hooks/useMediaQuery'
import { highlightTextMultiple } from '../utils/textHighlight'
import { protectedFileHref } from '../utils/fileDownload'
import './DirectorView.css'
import '../styles/common-buttons.css'

interface DirectorViewProps {
  purchaseRole?: string
}

function DirectorView({ purchaseRole = 'director' }: DirectorViewProps) {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandled = useRef<string | null>(null)
  const [requests, setRequests] = useState<PurchaseRequest[]>([])
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [ticketsLoading, setTicketsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'purchases' | 'tickets'>('purchases')
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [ticketsViewMode, setTicketsViewMode] = useState<'cards' | 'table'>('cards')
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [notes, setNotes] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [ticketsStatusFilter, setTicketsStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'completed'>('all')
  const [ticketsCardStatusFilter, setTicketsCardStatusFilter] = useState<string | null>(null)
  const [ticketsBuildingFilter, setTicketsBuildingFilter] = useState<string>('all')
  const [ticketsAdminFilter, setTicketsAdminFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [ticketsCurrentPage, setTicketsCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sectionPages, setSectionPages] = useState<Record<string, number>>({})
  const [showStatistics, setShowStatistics] = useState(false)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    if (user) {
      loadRequestsData()
      if (activeTab === 'tickets') {
        loadTicketsData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, purchaseRole, activeTab])

  // Deep-link z ranního digestu: ?id=
  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || loading) return
    if (deepLinkHandled.current === id) return
    deepLinkHandled.current = id

    const found = requests.find((r) => r.id === id)
    setActiveTab('purchases')
    if (found) {
      if (found.status === 'approved_by_economist') {
        setStatusFilter('approved_by_economist')
        setSelectedRequest(found)
        setActionType('approve')
        setNotes('')
      } else {
        setStatusFilter(found.status)
        setSelectedRequest(found)
        setActionType(null)
        alert('Tato žádost už není ve frontě ke schválení (stav: ' + getStatusLabel(found.status) + ').')
      }
    } else {
      alert('Žádost nenalezena nebo již byla zpracována.')
    }

    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, requests])

  const loadRequestsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const effectiveRole = purchaseRole || user.role
      const data = await loadPurchaseRequests(user.id, user.role ?? null, effectiveRole)
      setRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí o nákup')
    } finally {
      setLoading(false)
    }
  }

  const loadTicketsData = async () => {
    if (!user) return
    
    try {
      setTicketsLoading(true)
      const data = await loadTickets(user.id, 'director', undefined)
      setTickets(data)
    } catch (error) {
      console.error('Chyba při načítání závad:', error)
      alert('Chyba při načítání závad')
    } finally {
      setTicketsLoading(false)
    }
  }

  const handleAccelerateTicket = async (ticketId: string) => {
    if (!confirm('Opravdu chcete požádat o urychlení této žádosti?')) {
      return
    }

    try {
      const result = await accelerateTicket(ticketId)
      alert(`Žádost o urychlení byla odeslána. Email byl odeslán ${result.emails_sent} ${result.emails_sent === 1 ? 'příjemci' : 'příjemcům'}.`)
      await loadTicketsData()
    } catch (error: any) {
      console.error('Chyba při žádosti o urychlení:', error)
      alert(error.message || 'Chyba při žádosti o urychlení')
    }
  }

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return

    try {
      if (actionType === 'approve') {
        await approvePurchaseRequestByDirector(selectedRequest.id, notes || undefined)
      } else if (actionType === 'reject') {
        await rejectPurchaseRequestByDirector(selectedRequest.id, notes || undefined)
      }
      
      setSelectedRequest(null)
      setActionType(null)
      setNotes('')
      await loadRequestsData()
      alert('Akce byla úspěšně provedena')
    } catch (error) {
      console.error('Chyba při provádění akce:', error)
      alert('Chyba při provádění akce')
    }
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'new': 'Nová',
      'deferred': 'Odloženo ekonomem',
      'approved_by_economist': 'Schváleno ekonomem',
      'rejected': 'Zamítnuto',
      'approved_by_director': 'Schváleno ředitelem',
      'rejected_by_director': 'Zamítnuto ředitelem',
      'accepted_by_referent': 'Přijato obchodním referentem',
      'ordered_by_referent': 'Objednáno obchodním referentem',
      'ready_by_referent': 'Připraveno obchodním referentem',
      'completed': 'Dokončeno'
    }
    return labels[status] || status
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'deferred':
        return 'status-deferred'
      case 'approved_by_economist':
      case 'approved_by_director':
        return 'status-approved'
      case 'rejected':
      case 'rejected_by_economist':
      case 'rejected_by_director':
        return 'status-rejected'
      case 'accepted_by_referent':
        return 'status-accepted'
      case 'ordered_by_referent':
        return 'status-ordered'
      case 'ready_by_referent':
        return 'status-ready'
      case 'completed':
        return 'status-completed'
      default:
        return ''
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('cs-CZ')
  }

  const handleTableAction = async (request: PurchaseRequest, action: string) => {
    setSelectedRequest(request)
    if (action === 'approve') {
      setActionType('approve')
    } else if (action === 'reject') {
      setActionType('reject')
    }
    setNotes('')
  }

  // Получаем уникальные здания для фильтра заявок
  const ticketBuildings = useMemo(() => {
    const buildingMap = new Map<string, string>()
    tickets.forEach(ticket => {
      if (ticket.building_id && ticket.building_name) {
        buildingMap.set(ticket.building_id, ticket.building_name)
      }
    })
    return Array.from(buildingMap.entries()).map(([id, name]) => ({ id, name }))
  }, [tickets])

  // Получаем уникальных администраторов для фильтра заявок
  const ticketAdministrators = useMemo(() => {
    const adminMap = new Map<string, { name: string, type?: string }>()
    tickets.forEach(ticket => {
      if (ticket.admin_id && ticket.admin_name) {
        if (!adminMap.has(ticket.admin_id)) {
          adminMap.set(ticket.admin_id, {
            name: ticket.admin_name,
            type: ticket.admin_type
          })
        }
      }
    })
    return Array.from(adminMap.entries()).map(([id, data]) => ({ 
      id, 
      name: data.name,
      type: data.type 
    }))
  }, [tickets])

  // Фильтрация заявок
  const filteredTickets = useMemo(() => {
    let filtered = [...tickets]

    // Фильтр по статусу (приоритет у карточки-фильтра)
    const effectiveStatusFilter = ticketsCardStatusFilter !== null 
      ? ticketsCardStatusFilter 
      : (ticketsStatusFilter !== 'all' ? ticketsStatusFilter : null)
    if (effectiveStatusFilter) {
      filtered = filtered.filter(t => t.status === effectiveStatusFilter)
    }

    // Фильтр по зданию
    if (ticketsBuildingFilter !== 'all') {
      filtered = filtered.filter(t => t.building_id === ticketsBuildingFilter)
    }

    // Фильтр по администратору
    if (ticketsAdminFilter !== 'all') {
      filtered = filtered.filter(t => t.admin_id === ticketsAdminFilter)
    }

    return filtered
  }, [tickets, ticketsCardStatusFilter, ticketsStatusFilter, ticketsBuildingFilter, ticketsAdminFilter])

  // Пагинация для тикетов
  const paginatedTickets = useMemo(() => {
    const startIndex = (ticketsCurrentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredTickets.slice(startIndex, endIndex)
  }, [filteredTickets, ticketsCurrentPage, itemsPerPage])

  const ticketsTotalPages = useMemo(() => {
    return Math.ceil(filteredTickets.length / itemsPerPage)
  }, [filteredTickets, itemsPerPage])

  // Сброс страницы тикетов при изменении фильтров
  useEffect(() => {
    setTicketsCurrentPage(1)
  }, [ticketsCardStatusFilter, ticketsStatusFilter, ticketsBuildingFilter, ticketsAdminFilter])

  // Фильтрация заявок только по поисковому тексту (для статистики)
  const requestsForStats = useMemo(() => {
    if (!requests || requests.length === 0) return []
    if (!searchText.trim()) return requests
    
    const searchLower = searchText.toLowerCase().trim()
    return requests.filter(r => {
      const subject = (r.subject || '').toLowerCase()
      const description = (r.description || '').toLowerCase()
      const reason = (r.reason || '').toLowerCase()
      const supplier = (r.supplier || '').toLowerCase()
      const centerName = (r.center_name || '').toLowerCase()
      const createdByName = (r.created_by_name || '').toLowerCase()
      const notes = (r.notes || '').toLowerCase()
      
      return subject.includes(searchLower) ||
             description.includes(searchLower) ||
             reason.includes(searchLower) ||
             supplier.includes(searchLower) ||
             centerName.includes(searchLower) ||
             createdByName.includes(searchLower) ||
             notes.includes(searchLower)
    })
  }, [requests, searchText])

  // Фильтрация заявок по выбранному статусу и поисковому тексту (для отображения)
  const filteredRequests = useMemo(() => {
    let filtered = requestsForStats
    
    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(r => {
        if (statusFilter === 'new') return r.status === 'new'
        if (statusFilter === 'approved_by_economist') return r.status === 'approved_by_economist'
        if (statusFilter === 'deferred') return r.status === 'deferred'
        if (statusFilter === 'rejected_by_economist') return r.status === 'rejected_by_economist'
        if (statusFilter === 'approved_by_director') return r.status === 'approved_by_director'
        if (statusFilter === 'rejected_by_director') return r.status === 'rejected_by_director'
        if (statusFilter === 'accepted_by_referent') return r.status === 'accepted_by_referent'
        if (statusFilter === 'ordered_by_referent') return r.status === 'ordered_by_referent'
        if (statusFilter === 'completed') return r.status === 'completed'
        return true
      })
    }
    
    return filtered
  }, [requestsForStats, statusFilter])

  const approvedByEconomistRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_economist'), [requestsForStats])
  const approvedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_director'), [requestsForStats])
  const rejectedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_director'), [requestsForStats])
  const acceptedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'accepted_by_referent'), [requestsForStats])
  const orderedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'ordered_by_referent'), [requestsForStats])
  const completedRequests = useMemo(() => requestsForStats.filter(r => r.status === 'completed'), [requestsForStats])

  // Функции для пагинации
  const getPaginatedRequests = (items: PurchaseRequest[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }

  const getTotalPages = (items: PurchaseRequest[]) => {
    return Math.ceil(items.length / itemsPerPage)
  }

  // Сбрасываем страницу при изменении фильтра или поиска
  useEffect(() => {
    setCurrentPage(1)
    setSectionPages({}) // Сбрасываем все страницы секций
  }, [statusFilter, searchText])

  const getSectionPage = (sectionKey: string) => {
    return sectionPages[sectionKey] || 1
  }

  const setSectionPage = (sectionKey: string, page: number) => {
    setSectionPages(prev => ({ ...prev, [sectionKey]: page }))
  }

  if (loading && activeTab === 'purchases') {
    return <div className="director-view">Načítání...</div>
  }

  if (ticketsLoading && activeTab === 'tickets') {
    return <div className="director-view">Načítání...</div>
  }

  return (
    <div className="director-view">
      <div className="view-controls" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => {
              setActiveTab('purchases')
              if (activeTab === 'tickets') {
                setTicketsLoading(false)
              }
            }}
            style={{
              background: activeTab === 'purchases' ? '#14b8a6' : '#e0e0e0',
              color: activeTab === 'purchases' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Žádanky o nákup
          </button>
          <button
            onClick={() => {
              setActiveTab('tickets')
              if (tickets.length === 0) {
                loadTicketsData()
              }
            }}
            style={{
              background: activeTab === 'tickets' ? '#14b8a6' : '#e0e0e0',
              color: activeTab === 'tickets' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Hlášení závad
          </button>
        </div>
        {activeTab === 'purchases' && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button 
              className={viewMode === 'cards' ? 'active' : ''}
              onClick={() => setViewMode('cards')}
            >
              Karty
            </button>
            <button 
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
            >
              Tabulka
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Hledat podle textu..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  minWidth: '250px'
                }}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    cursor: 'pointer'
                  }}
                >
                  Vymazat
                </button>
              )}
              <button
                onClick={() => setShowStatistics(true)}
                style={{
                  background: '#14b8a6',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0d9488'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#14b8a6'
                }}
              >
                📊 Statistika řešitelů
              </button>
              <button
                onClick={() => window.location.href = '/public-canteen/statistics'}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                📊 Statistiky jídelny
              </button>
            </div>
          </div>
        )}
        {activeTab === 'tickets' && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className={ticketsViewMode === 'cards' ? 'active' : ''}
                onClick={() => setTicketsViewMode('cards')}
              >
                📋 Karty
              </button>
              <button 
                className={ticketsViewMode === 'table' ? 'active' : ''}
                onClick={() => setTicketsViewMode('table')}
              >
                📊 Tabulka
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={ticketsStatusFilter}
                onChange={(e) => {
                  setTicketsStatusFilter(e.target.value as 'all' | 'new' | 'in_progress' | 'completed')
                  setTicketsCardStatusFilter(null)
                }}
                style={{
                  padding: '0.5rem 1rem',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="all">Všechny statusy</option>
                <option value="new">Nové</option>
                <option value="in_progress">V řešení</option>
                <option value="completed">Dokončené</option>
              </select>
              <select
                value={ticketsBuildingFilter}
                onChange={(e) => setTicketsBuildingFilter(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="all">Všechna budovy</option>
                {ticketBuildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
              <select
                value={ticketsAdminFilter}
                onChange={(e) => setTicketsAdminFilter(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="all">Všichni administrátoři</option>
                {ticketAdministrators.map(admin => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name} {admin.type === 'spravce_it' ? '(IT)' : admin.type === 'spravce_budov' ? '(Budovy)' : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowStatistics(true)}
                style={{
                  background: '#14b8a6',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0d9488'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#14b8a6'
                }}
              >
                📊 Statistika řešitelů
              </button>
              <button
                onClick={() => window.location.href = '/public-canteen/statistics'}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5568d3'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea'
                }}
              >
                📊 Statistiky jídelny
              </button>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'tickets' ? (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem', 
            marginBottom: '2rem' 
          }}>
            <div 
              onClick={() => {
                setTicketsCardStatusFilter(ticketsCardStatusFilter === 'new' ? null : 'new')
                setTicketsStatusFilter('all')
              }}
              style={{
                background: ticketsCardStatusFilter === 'new' ? '#14b8a6' : 'white',
                color: ticketsCardStatusFilter === 'new' ? 'white' : '#333',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {tickets.filter(t => t.status === 'new').length}
              </div>
              <div style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nové</div>
            </div>
            <div 
              onClick={() => {
                setTicketsCardStatusFilter(ticketsCardStatusFilter === 'in_progress' ? null : 'in_progress')
                setTicketsStatusFilter('all')
              }}
              style={{
                background: ticketsCardStatusFilter === 'in_progress' ? '#14b8a6' : 'white',
                color: ticketsCardStatusFilter === 'in_progress' ? 'white' : '#333',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {tickets.filter(t => t.status === 'in_progress').length}
              </div>
              <div style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>V řešení</div>
            </div>
            <div 
              onClick={() => {
                setTicketsCardStatusFilter(ticketsCardStatusFilter === 'completed' ? null : 'completed')
                setTicketsStatusFilter('all')
              }}
              style={{
                background: ticketsCardStatusFilter === 'completed' ? '#14b8a6' : 'white',
                color: ticketsCardStatusFilter === 'completed' ? 'white' : '#333',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {tickets.filter(t => t.status === 'completed').length}
              </div>
              <div style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dokončené</div>
            </div>
            <div 
              onClick={() => {
                setTicketsCardStatusFilter(null)
                setTicketsStatusFilter('all')
              }}
              style={{
                background: ticketsCardStatusFilter === null ? '#14b8a6' : 'white',
                color: ticketsCardStatusFilter === null ? 'white' : '#333',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {tickets.length}
              </div>
              <div style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Celkem</div>
            </div>
          </div>
          {ticketsViewMode === 'cards' ? (
            <>
              <TicketList 
                tickets={paginatedTickets} 
                showAssignButton={false}
                showCompleteButton={false}
                showAccelerateButton={true}
                onAccelerate={handleAccelerateTicket}
              />
              {ticketsTotalPages > 1 && (
                <Pagination
                  currentPage={ticketsCurrentPage}
                  totalPages={ticketsTotalPages}
                  onPageChange={setTicketsCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredTickets.length}
                />
              )}
            </>
          ) : (
            <>
              <TicketsTable 
                tickets={paginatedTickets} 
                showCompleteButton={false}
                showAccelerateButton={true}
                onAccelerate={handleAccelerateTicket}
                filterType="admin"
              />
              {ticketsTotalPages > 1 && (
                <Pagination
                  currentPage={ticketsCurrentPage}
                  totalPages={ticketsTotalPages}
                  onPageChange={setTicketsCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredTickets.length}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <>

      <div className="admin-stats">
        <div 
          className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Nové</h3>
          <p className="stat-number">{requestsForStats.filter(r => r.status === 'new').length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'approved_by_economist' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'approved_by_economist' ? null : 'approved_by_economist')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Schváleno ekonomem</h3>
          <p className="stat-number">{approvedByEconomistRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'deferred' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'deferred' ? null : 'deferred')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Odloženo ekonomem</h3>
          <p className="stat-number">{requestsForStats.filter(r => r.status === 'deferred').length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'rejected_by_economist' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'rejected_by_economist' ? null : 'rejected_by_economist')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Zamítnuto ekonomem</h3>
          <p className="stat-number">{requestsForStats.filter(r => r.status === 'rejected_by_economist').length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'approved_by_director' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'approved_by_director' ? null : 'approved_by_director')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Schváleno ředitelem</h3>
          <p className="stat-number">{approvedByDirectorRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'rejected_by_director' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'rejected_by_director' ? null : 'rejected_by_director')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Zamítnuto ředitelem</h3>
          <p className="stat-number">{rejectedByDirectorRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'accepted_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'accepted_by_referent' ? null : 'accepted_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Přijato obchodním referentem</h3>
          <p className="stat-number">{acceptedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'ordered_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'ordered_by_referent' ? null : 'ordered_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Objednáno obchodním referentem</h3>
          <p className="stat-number">{orderedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Dokončeno</h3>
          <p className="stat-number">{completedRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === null ? 'active' : ''}`}
          onClick={() => setStatusFilter(null)}
          style={{ cursor: 'pointer' }}
        >
          <h3>Celkem</h3>
          <p className="stat-number">{requestsForStats.length}</p>
        </div>
      </div>

      {viewMode === 'table' ? (
        <PurchaseRequestsTable 
          requests={filteredRequests}
          onAction={handleTableAction}
          filterType="director"
          hideStatusFilter={true}
          statusFilter={statusFilter}
        />
      ) : statusFilter ? (
        <div className="requests-container">
          {statusFilter === 'approved_by_economist' && (
            <>
              <h2>Schváleno ekonomem ({approvedByEconomistRequests.length})</h2>
              {approvedByEconomistRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(approvedByEconomistRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <>
                          <button 
                            className="btn-approve"
                            onClick={() => {
                              setSelectedRequest(request)
                              setActionType('approve')
                              setNotes('')
                            }}
                          >
                            Schválit
                          </button>
                          <button 
                            className="btn-reject"
                            onClick={() => {
                              setSelectedRequest(request)
                              setActionType('reject')
                              setNotes('')
                            }}
                          >
                            Zamítnout
                          </button>
                        </>
                      }
                    />
                  ))}
                  </div>
                  {getTotalPages(approvedByEconomistRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(approvedByEconomistRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={approvedByEconomistRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti k posouzení</p>
              )}
            </>
          )}
          {statusFilter === 'new' && (
            <div className="requests-section">
              <h2>Nové žádosti ({filteredRequests.filter(r => r.status === 'new').length})</h2>
              {filteredRequests.filter(r => r.status === 'new').length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(filteredRequests.filter(r => r.status === 'new'), currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(filteredRequests.filter(r => r.status === 'new')) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(filteredRequests.filter(r => r.status === 'new'))}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredRequests.filter(r => r.status === 'new').length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné nové žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'deferred' && (
            <div className="requests-section">
              <h2>Odložené žádosti ({filteredRequests.filter(r => r.status === 'deferred').length})</h2>
              {filteredRequests.filter(r => r.status === 'deferred').length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(filteredRequests.filter(r => r.status === 'deferred'), currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(filteredRequests.filter(r => r.status === 'deferred')) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(filteredRequests.filter(r => r.status === 'deferred'))}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredRequests.filter(r => r.status === 'deferred').length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné odložené žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'rejected_by_economist' && (
            <div className="requests-section">
              <h2>Zamítnuto ekonomem ({filteredRequests.filter(r => r.status === 'rejected_by_economist').length})</h2>
              {filteredRequests.filter(r => r.status === 'rejected_by_economist').length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(filteredRequests.filter(r => r.status === 'rejected_by_economist'), currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(filteredRequests.filter(r => r.status === 'rejected_by_economist')) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(filteredRequests.filter(r => r.status === 'rejected_by_economist'))}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredRequests.filter(r => r.status === 'rejected_by_economist').length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné zamítnuté žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'approved_by_director' && (
            <div className="requests-section">
              <h2>Schváleno ředitelem ({approvedByDirectorRequests.length})</h2>
              {approvedByDirectorRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(approvedByDirectorRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(approvedByDirectorRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(approvedByDirectorRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={approvedByDirectorRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné schválené žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'rejected_by_director' && (
            <div className="requests-section">
              <h2>Zamítnuto ředitelem ({rejectedByDirectorRequests.length})</h2>
              {rejectedByDirectorRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(rejectedByDirectorRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(rejectedByDirectorRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(rejectedByDirectorRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={rejectedByDirectorRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné zamítnuté žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'accepted_by_referent' && (
            <div className="requests-section">
              <h2>Přijato obchodním referentem ({acceptedByReferentRequests.length})</h2>
              {acceptedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(acceptedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(acceptedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(acceptedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={acceptedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné přijaté žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'ordered_by_referent' && (
            <div className="requests-section">
              <h2>Objednáno obchodním referentem ({orderedByReferentRequests.length})</h2>
              {orderedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(orderedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(orderedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(orderedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={orderedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné objednané žádosti</p>
              )}
            </div>
          )}
          {statusFilter === 'completed' && (
            <div className="requests-section">
              <h2>Dokončené žádosti ({completedRequests.length})</h2>
              {completedRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(completedRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(completedRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(completedRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={completedRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
        <div className="requests-container">
          <h2>Schváleno ekonomem ({approvedByEconomistRequests.length})</h2>
          {approvedByEconomistRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(approvedByEconomistRequests, getSectionPage('approved_economist')).map(request => (
                <div key={request.id} className="request-card">
                  <div className="request-header">
                    <h3>{highlightTextMultiple(request.subject, searchText)}</h3>
                    <span className={`status-badge status-${request.status}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </div>
                  <div className="request-details">
                    <p><strong>Středisko:</strong> {highlightTextMultiple(request.center_name, searchText)}</p>
                    <p><strong>Vytvořil:</strong> {highlightTextMultiple(request.created_by_name, searchText)}</p>
                    <p><strong>Datum vytvoření:</strong> {formatDate(request.created_at)}</p>
                    <p><strong>Schváleno ekonomem:</strong> {highlightTextMultiple(request.approved_by_economist_name, searchText)} ({formatDate(request.approved_by_economist_at)})</p>
                    {request.notes && (
                      <div className="request-notes-display">
                        <strong>Poznámky:</strong>
                        <div className="notes-content">{highlightTextMultiple(request.notes, searchText)}</div>
                      </div>
                    )}
                    <p><strong>Popis:</strong> {highlightTextMultiple(request.description, searchText)}</p>
                    <p><strong>Důvod objednání:</strong> {highlightTextMultiple(request.reason, searchText)}</p>
                    {request.supplier && <p><strong>Dodavatel:</strong> {highlightTextMultiple(request.supplier, searchText)}</p>}
                    {request.amount && <p><strong>Částka:</strong> {request.amount.toLocaleString('cs-CZ')} Kč</p>}
                    {request.file_name && (
                      <p>
                        <strong>Příloha:</strong>{' '}
                        <a 
                          href={protectedFileHref(request.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {request.file_name}
                        </a>
                      </p>
                    )}
                  </div>
                  <div className="request-actions">
                    <button 
                      className="btn-approve"
                      onClick={() => {
                        setSelectedRequest(request)
                        setActionType('approve')
                        setNotes('')
                      }}
                    >
                      Schválit
                    </button>
                    <button 
                      className="btn-reject"
                      onClick={() => {
                        setSelectedRequest(request)
                        setActionType('reject')
                        setNotes('')
                      }}
                    >
                      Zamítnout
                    </button>
                      </div>
                    </div>
                  ))}
              </div>
              {getTotalPages(approvedByEconomistRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('approved_economist')}
                  totalPages={getTotalPages(approvedByEconomistRequests)}
                  onPageChange={(page) => setSectionPage('approved_economist', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={approvedByEconomistRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné žádosti k posouzení</p>
          )}

          <h2>Schváleno ředitelem ({approvedByDirectorRequests.length})</h2>
          {approvedByDirectorRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(approvedByDirectorRequests, getSectionPage('approved_director')).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                ))}
              </div>
              {getTotalPages(approvedByDirectorRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('approved_director')}
                  totalPages={getTotalPages(approvedByDirectorRequests)}
                  onPageChange={(page) => setSectionPage('approved_director', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={approvedByDirectorRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné schválené žádosti ředitelem</p>
          )}

          <h2>Zamítnuto ředitelem ({rejectedByDirectorRequests.length})</h2>
          {rejectedByDirectorRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(rejectedByDirectorRequests, getSectionPage('rejected_director')).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                ))}
              </div>
              {getTotalPages(rejectedByDirectorRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('rejected_director')}
                  totalPages={getTotalPages(rejectedByDirectorRequests)}
                  onPageChange={(page) => setSectionPage('rejected_director', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={rejectedByDirectorRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné zamítnuté žádosti ředitelem</p>
          )}

          <h2>Přijato obchodním referentem ({acceptedByReferentRequests.length})</h2>
          {acceptedByReferentRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(acceptedByReferentRequests, getSectionPage('accepted_referent')).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                ))}
              </div>
              {getTotalPages(acceptedByReferentRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('accepted_referent')}
                  totalPages={getTotalPages(acceptedByReferentRequests)}
                  onPageChange={(page) => setSectionPage('accepted_referent', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={acceptedByReferentRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné přijaté žádosti</p>
          )}

          <h2>Objednáno obchodním referentem ({orderedByReferentRequests.length})</h2>
          {orderedByReferentRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(orderedByReferentRequests, getSectionPage('ordered_referent')).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                ))}
              </div>
              {getTotalPages(orderedByReferentRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('ordered_referent')}
                  totalPages={getTotalPages(orderedByReferentRequests)}
                  onPageChange={(page) => setSectionPage('ordered_referent', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={orderedByReferentRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné objednané žádosti</p>
          )}

          <h2>Dokončeno ({completedRequests.length})</h2>
          {completedRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(completedRequests, getSectionPage('completed')).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                ))}
              </div>
              {getTotalPages(completedRequests) > 1 && (
                <Pagination
                  currentPage={getSectionPage('completed')}
                  totalPages={getTotalPages(completedRequests)}
                  onPageChange={(page) => setSectionPage('completed', page)}
                  itemsPerPage={itemsPerPage}
                  totalItems={completedRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
          )}
        </div>
        </>
      )}
        </>
      )}

      {selectedRequest && actionType && (
        <div className="modal-overlay" onClick={() => {
          setSelectedRequest(null)
          setActionType(null)
          setNotes('')
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {actionType === 'approve' && 'Schválit žádost'}
              {actionType === 'reject' && 'Zamítnout žádost'}
            </h3>
            <div className="modal-request-info">
              <p><strong>Předmět:</strong> {selectedRequest.subject}</p>
              <p><strong>Středisko:</strong> {selectedRequest.center_name}</p>
            </div>
            <div className="modal-form">
              <label>
                Poznámky (volitelné):
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Zadejte poznámky..."
                />
              </label>
              <div className="modal-actions">
                <button 
                  className={`btn-${actionType === 'approve' ? 'approve' : 'reject'}`}
                  onClick={handleAction}
                >
                  {actionType === 'approve' && 'Schválit'}
                  {actionType === 'reject' && 'Zamítnout'}
                </button>
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setSelectedRequest(null)
                    setActionType(null)
                    setNotes('')
                  }}
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStatistics && (
        <ExecutorStatistics onClose={() => setShowStatistics(false)} />
      )}
    </div>
  )
}

export default DirectorView

